export type FormatCurrencyOptions = {
  currency?: string;
  locale?: string;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
};

/**
 * Format a number as currency using Intl.NumberFormat
 * - Returns empty string for null/undefined/NaN
 * - Defaults to en-US and USD with 2 fraction digits
 */
export function formatCurrency(
  amount: number | null | undefined,
  options: FormatCurrencyOptions = {},
): string {
  if (amount === null || amount === undefined) return '';

  const numericAmount = Number(amount);
  if (Number.isNaN(numericAmount)) return '';

  const {
    currency = 'USD',
    locale = 'en-US',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2,
  } = options;

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits,
      maximumFractionDigits,
    }).format(numericAmount);
  } catch (error) {
    // Fallback formatting if Intl throws for some reason
    return `${currency} ${numericAmount.toFixed(minimumFractionDigits)}`;
  }
}

/**
 * Format currency compactly for card display ($1.2M, $45.5K, $999.00)
 * Use in stat cards and dashboards where space is limited.
 * Pair with formatFullCurrency in a `title` attribute for hover detail.
 */
export function formatCompactCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';

  if (num >= 1_000_000) {
    const millions = num / 1_000_000;
    return `$${millions % 1 === 0 ? millions.toFixed(0) : millions.toFixed(1)}M`;
  }
  if (num >= 10_000) {
    const thousands = num / 1_000;
    return `$${thousands % 1 === 0 ? thousands.toFixed(0) : thousands.toFixed(1)}K`;
  }
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Format currency with full precision for tooltips/hover detail.
 */
export function formatFullCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '';
  const num = Number(value);
  if (Number.isNaN(num)) return '';
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default formatCurrency;
